/**
 * Stops a missed file drop from throwing away someone's unsaved work.
 *
 * Default browser behaviour when a file is dropped on a page that has not
 * claimed the drop is to open that file in the tab — replacing whatever screen
 * the person was on. In HMS that means a half-filled delivery, recap or invoice
 * form is simply gone, with no warning and nothing to undo. Anyone who aims a
 * little wide of an upload box pays for it.
 *
 * This guard claims drops for the whole window and does nothing with them, so a
 * near-miss is a no-op instead of a data loss. Real drop zones are excluded by
 * the `data-file-drop-zone` marker that FileDropZone renders, so they keep
 * receiving their files exactly as before — the check is belt and braces on top
 * of the zone's own stopPropagation, so a zone that forgets to stop the event
 * still works.
 */

const isInsideDropZone = (target) =>
    !!(target && typeof target.closest === 'function' && target.closest('[data-file-drop-zone]'));

const handleDragOver = (e) => {
    if (isInsideDropZone(e.target)) return;
    e.preventDefault();
    if (e.dataTransfer) {
        // Show the "you cannot drop here" cursor rather than implying the whole
        // app is a drop target.
        e.dataTransfer.dropEffect = 'none';
    }
};

const handleDrop = (e) => {
    if (isInsideDropZone(e.target)) return;
    e.preventDefault();
};

/**
 * Attaches the guard. Returns a function that removes it again.
 */
export const installStrayDropGuard = (target = window) => {
    if (!target || typeof target.addEventListener !== 'function') {
        return () => {};
    }
    target.addEventListener('dragover', handleDragOver);
    target.addEventListener('drop', handleDrop);
    return () => {
        target.removeEventListener('dragover', handleDragOver);
        target.removeEventListener('drop', handleDrop);
    };
};

export default installStrayDropGuard;
